import { useEffect, useMemo, useState } from 'react'
import type { FeePlanInput } from '@academy/types'
import { splitBetweenDates } from '../lib/installments.js'
import { Card, CardBody, CardHeader } from './Card.js'
import { Field, Input, Checkbox } from './Field.js'
import { Money } from './Money.js'

interface FeePlanSectionProps {
  /** Emits the assembled plan when valid+enabled, else null (no plan). */
  onChange: (plan: FeePlanInput | null) => void
  /** Initial values when editing an existing plan. */
  initial?: Partial<{
    totalRupees: string
    discountRupees: string
    startDate: string
    endDate: string
    installments: { amountRupees: string; generateOn: string }[]
    notes: string
  }>
  /** Force the section open (e.g. the standalone editor). Enrolment defaults closed. */
  alwaysOn?: boolean
}

const todayIso = () => new Date().toISOString().slice(0, 10)
const rupeesToPaise = (s: string) => Math.round((parseFloat(s) || 0) * 100)

interface Row {
  amountRupees: string
  generateOn: string
}

/**
 * Per-student fee plan editor. The admin enters a total, a value discount, a
 * start→end date range and a number of installments; the rows auto-fill — equal
 * amounts (remainder on #1) spread evenly between the dates — and stay editable.
 * Emits a FeePlanInput when the schedule reconciles to (total - discount) and the
 * date range is valid, else null. Total is typed directly (decoupled from the
 * per-class price book).
 */
export function FeePlanSection({ onChange, initial, alwaysOn }: FeePlanSectionProps) {
  const [enabled, setEnabled] = useState(!!alwaysOn || !!initial)
  const [totalRupees, setTotalRupees] = useState(initial?.totalRupees ?? '')
  const [discountRupees, setDiscountRupees] = useState(initial?.discountRupees ?? '')
  const [startDate, setStartDate] = useState(initial?.startDate ?? todayIso())
  const [endDate, setEndDate] = useState(initial?.endDate ?? '')
  const [count, setCount] = useState(String(initial?.installments?.length ?? 1))
  const [rows, setRows] = useState<Row[]>(initial?.installments ?? [])

  const netPaise = Math.max(0, rupeesToPaise(totalRupees) - rupeesToPaise(discountRupees))
  const rowsSumPaise = rows.reduce((s, r) => s + rupeesToPaise(r.amountRupees), 0)
  const datesValid = !!startDate && !!endDate && endDate >= startDate
  // A plan is valid once there's at least one dated installment with a positive
  // amount. The schedule total need NOT equal the header total — per-interval
  // amounts are free (the header total is informational). The banner below shows
  // the two figures side by side so the admin can see any intended difference.
  const rowsValid =
    rows.length > 0 &&
    rows.every((r) => rupeesToPaise(r.amountRupees) > 0 && !!r.generateOn)
  const planValid = rowsValid && datesValid
  const reconciles = rowsSumPaise === netPaise && netPaise > 0

  // Auto-fill rows: equal amounts spread evenly between start and end.
  const autofill = () => {
    if (!datesValid) return
    const n = Math.max(1, parseInt(count, 10) || 1)
    setRows(
      splitBetweenDates(netPaise, n, startDate, endDate).map((t) => ({
        amountRupees: t.amountRupees,
        generateOn: t.dueDate,
      })),
    )
  }

  // Emit upward whenever the assembled plan changes.
  const plan = useMemo<FeePlanInput | null>(() => {
    if (!enabled || !planValid) return null
    return {
      totalAmountInr: rupeesToPaise(totalRupees),
      discountInr: rupeesToPaise(discountRupees),
      startDate,
      endDate,
      installments: rows.map((r) => ({
        amountInr: rupeesToPaise(r.amountRupees),
        generateOn: r.generateOn,
      })),
    }
  }, [enabled, planValid, totalRupees, discountRupees, startDate, endDate, rows])

  useEffect(() => {
    onChange(plan)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan])

  return (
    <Card>
      <CardHeader title="Fee plan" description="Optional. Schedule invoices that auto-generate and notify the parent on each date." />
      <CardBody className="space-y-4">
        {!alwaysOn ? (
          <Checkbox
            label="Set up a fee plan for this student"
            checked={enabled}
            onChange={setEnabled}
          />
        ) : null}

        {enabled ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Total fee (₹)" required>
                <Input
                  numeric
                  inputMode="decimal"
                  value={totalRupees}
                  onChange={(e) => setTotalRupees(e.target.value)}
                  placeholder="60000"
                />
              </Field>
              <Field label="Discount (₹)" optional hint="In value, not percentage">
                <Input
                  numeric
                  inputMode="decimal"
                  value={discountRupees}
                  onChange={(e) => setDiscountRupees(e.target.value)}
                  placeholder="5000"
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Start date" required>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Field>
              <Field
                label="End date"
                required
                error={
                  startDate && endDate && endDate < startDate
                    ? 'Must be on or after start'
                    : undefined
                }
              >
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Field>
              <Field label="Installments" hint="Split evenly across the range">
                <Input
                  numeric
                  inputMode="numeric"
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                />
              </Field>
            </div>

            <button
              type="button"
              onClick={autofill}
              className="text-sm text-[var(--color-primary)] hover:underline"
            >
              Generate schedule →
            </button>

            {rows.length > 0 ? (
              <div className="space-y-2">
                {rows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 text-xs text-fg-subtle">#{i + 1}</span>
                    <Input
                      type="date"
                      value={row.generateOn}
                      onChange={(e) =>
                        setRows((rs) => rs.map((r, j) => (j === i ? { ...r, generateOn: e.target.value } : r)))
                      }
                      className="flex-1"
                    />
                    <Input
                      numeric
                      inputMode="decimal"
                      value={row.amountRupees}
                      onChange={(e) =>
                        setRows((rs) => rs.map((r, j) => (j === i ? { ...r, amountRupees: e.target.value } : r)))
                      }
                      className="w-32"
                      placeholder="₹"
                    />
                  </div>
                ))}
                <div
                  className={`flex justify-between text-sm ${
                    reconciles ? 'text-success' : 'text-fg-muted'
                  }`}
                >
                  <span>Schedule total</span>
                  <span>
                    <Money paise={rowsSumPaise} variant="data" /> / net{' '}
                    <Money paise={netPaise} variant="data" />
                  </span>
                </div>
                {!reconciles ? (
                  <p className="text-xs text-fg-subtle">
                    The schedule bills {`₹`}
                    {(rowsSumPaise / 100).toLocaleString('en-IN')} in total. It does not
                    have to match the header total — set different amounts per interval
                    if you like.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-fg-subtle">
                Enter a total and click “Generate schedule”.
              </p>
            )}
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
